/**
 * Getting a diagram in and out of the database.
 *
 * Pure but for the client passed in, so the save rules — when to write,
 * what a conflict means, what a document is allowed to contain — are
 * testable without a browser or a network. They are the rules that go
 * wrong quietly: an autosave that fires on every pixel of a drag, or one
 * that decides a document is unchanged when it is not.
 */

/** The empty document, matching the column default exactly. */
export function emptyDoc() {
  return { shapes: [], edges: [], nextZ: 1 };
}

/**
 * What the anchor columns should be for a place in the app.
 *
 * Ported from Shadowland Title, where a chain hung off a project and
 * optionally one of its subject tracts. Here the deal is the project and
 * an Interest Estimator row is the tract: a deal with no row named is the
 * whole deal's chain, a deal with one is that tract's. Same shape, same
 * one-diagram-per-chain rule — this is the pair of columns the unique
 * indexes are built on, so it stays exactly two dimensions.
 */
export function anchorFor({ dealId = null, interestEstimateId = null } = {}) {
  return {
    deal_id: dealId || null,
    interest_estimate_id: interestEstimateId || null,
  };
}

/** A PostgREST filter for exactly that anchor, nulls included. */
export function anchorFilter(query, anchor) {
  let out = query;
  for (const [column, value] of Object.entries(anchor)) {
    out = value === null ? out.is(column, null) : out.eq(column, value);
  }
  return out;
}

/**
 * A document read back from the database, made safe to render.
 *
 * The column has a shape check on it, so this is not defending against
 * the database — it is defending against a row written by an older
 * version of the app, which is the case that actually happens. A missing
 * `nextZ` would put every new shape at z 0 and stack the whole chart in
 * the order it happened to load.
 */
export function normalizeDoc(raw) {
  const doc = raw && typeof raw === 'object' ? raw : {};
  const shapes = Array.isArray(doc.shapes) ? doc.shapes : [];
  const edges = Array.isArray(doc.edges) ? doc.edges : [];
  const highest = Math.max(0, ...shapes.map((s) => Number(s.z) || 0), ...edges.map((e) => Number(e.z) || 0));
  const next = Number(doc.nextZ);
  return {
    shapes,
    edges,
    nextZ: Number.isFinite(next) && next > highest ? next : highest + 1,
  };
}

/**
 * Has anything worth saving actually changed?
 *
 * Compared as JSON rather than by reference: every pointer move rebuilds
 * the document object, so a reference check would autosave continuously
 * through a drag and a value check saves once when it stops.
 */
export function docChanged(a, b) {
  return JSON.stringify(a) !== JSON.stringify(b);
}

// Long enough that a drag, a resize and a burst of typing are one save;
// short enough that a closed tab loses a moment rather than an hour.
export const SAVE_AFTER_MS = 1200;

/**
 * How long a load may take before the screen gives up on it.
 *
 * A request that never comes back leaves a spinner spinning, and a spinner
 * with no end is the one failure a person cannot act on: no message, no
 * retry, nothing to report beyond "it says Loading". Fifteen seconds is far
 * longer than this query has ever taken and far shorter than somebody will
 * sit staring at it.
 */
export const DIAGRAM_LOAD_TIMEOUT_MS = 15000;

export function withTimeout(promise, ms = DIAGRAM_LOAD_TIMEOUT_MS, message = 'timed out') {
  let timer = null;
  return Promise.race([
    Promise.resolve(promise).finally(() => { if (timer) clearTimeout(timer); }),
    new Promise((_resolve, reject) => {
      timer = setTimeout(() => reject(new Error(message)), ms);
    }),
  ]);
}

/**
 * Load the diagram for a place, creating it the first time.
 *
 * The create is attempted rather than checked-then-inserted: two tabs
 * opening the same tract at once would both find nothing and both
 * insert. The unique index refuses the second, and this reads the row
 * the winner made instead of surfacing an error nobody can act on.
 */
export async function loadDiagram(supabase, place) {
  const anchor = anchorFor(place);
  const found = await anchorFilter(
    supabase.from('canvas_diagrams').select('id, title, doc, version'), anchor,
  ).maybeSingle();
  if (found.error) return { error: found.error };
  if (found.data) {
    return { data: { ...found.data, doc: normalizeDoc(found.data.doc) } };
  }

  const made = await supabase
    .from('canvas_diagrams')
    .insert({ ...anchor, title: place.title || '', created_by: place.userId || null })
    .select('id, title, doc, version')
    .maybeSingle();
  if (made.data) return { data: { ...made.data, doc: normalizeDoc(made.data.doc) } };

  // Somebody else won the race. Read theirs.
  const again = await anchorFilter(
    supabase.from('canvas_diagrams').select('id, title, doc, version'), anchor,
  ).maybeSingle();
  if (again.data) return { data: { ...again.data, doc: normalizeDoc(again.data.doc) } };
  return { error: made.error || again.error || new Error('The diagram could not be opened.') };
}

/**
 * Save, and say plainly when somebody else got there first.
 *
 * The function returns no row when the version has moved on. That is not
 * an error to retry — retrying is precisely the overwrite the version
 * exists to prevent — so it comes back as a conflict the screen can
 * offer to reload.
 */
export async function saveDiagram(supabase, { id, doc, version, title = null }) {
  const { data, error } = await supabase.rpc('canvas_save_diagram', {
    p_id: id, p_doc: doc, p_version: version, p_title: title,
  });
  if (error) return { error };
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) return { conflict: true };
  return { data: { id: row.id, title: row.title, version: row.version } };
}
